import React from 'react';
import { ChevronRight } from 'lucide-react';
import { Switch } from '../ui/switch';
import { cn } from '../../lib/utils';

export const SettingsSection = ({ title, children, className }) => {
  return (
    <div className={cn("space-y-2", className)}>
      {title && (
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1">
          {title}
        </h3>
      )}
      <div className="bg-card rounded-2xl overflow-hidden">
        {children}
      </div>
    </div>
  );
};

export const SettingsRow = ({
  label,
  value,
  description,
  icon,
  chevron,
  accessory,
  onClick,
  className,
  disabled
}) => {
  const Component = onClick ? 'button' : 'div';
  
  return (
    <Component
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        "w-full flex items-center justify-between gap-3 px-4 py-3 min-h-[48px]",
        "text-left transition-colors border-b border-border/30 last:border-b-0",
        onClick && !disabled && "active:bg-muted/50",
        disabled && "opacity-50",
        className
      )}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {icon && (
          <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            {icon}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-normal">{label}</p>
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 min-w-0">
        {value && (
          <span className="text-[15px] text-muted-foreground truncate">{value}</span>
        )}
        {accessory}
        {chevron && (
          <ChevronRight className="w-5 h-5 text-muted-foreground/50" />
        )}
      </div>
    </Component>
  );
};

export const ToggleRow = ({
  label,
  description,
  icon,
  checked,
  onCheckedChange,
  disabled,
  className
}) => {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 px-4 py-3 min-h-[48px]",
        "border-b border-border/30 last:border-b-0",
        disabled && "opacity-50",
        className
      )}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {icon && (
          <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            {icon}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-normal">{label}</p>
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
    </div>
  );
};

export const StatusBadge = ({ status, className }) => {
  const variants = {
    connected: 'bg-green-500/15 text-green-600',
    running: 'bg-green-500/15 text-green-600',
    active: 'bg-green-500/15 text-green-600',
    enabled: 'bg-green-500/15 text-green-600',
    paired: 'bg-blue-500/15 text-blue-600',
    configured: 'bg-green-500/15 text-green-600',
    disconnected: 'bg-red-500/15 text-red-600',
    stopped: 'bg-red-500/15 text-red-600',
    unreachable: 'bg-red-500/15 text-red-600',
    inactive: 'bg-muted text-muted-foreground',
    disabled: 'bg-muted text-muted-foreground',
    unknown: 'bg-muted text-muted-foreground',
    pending: 'bg-amber-500/15 text-amber-600',
    error: 'bg-amber-500/15 text-amber-600',
  };

  // Friendly labels for the service statuses; anything else renders as-is so
  // existing callers (connected, paired, ...) are unaffected.
  const labels = {
    running: 'Running',
    stopped: 'Stopped',
    unreachable: 'Offline',
    error: 'Error',
    unknown: 'Unknown',
  };

  const key = status?.toLowerCase();
  const variant = variants[key] || variants.unknown;

  return (
    <span className={cn(
      "px-2.5 py-1 rounded-full text-xs font-medium",
      variant,
      className
    )}>
      {labels[key] || status}
    </span>
  );
};

export const DangerSection = ({ title = "Danger Zone", children, className }) => {
  return (
    <div className={cn("space-y-2", className)}>
      <h3 className="text-xs font-medium text-destructive uppercase tracking-wide px-1">
        {title}
      </h3>
      <div className="bg-card rounded-2xl overflow-hidden border border-destructive/20">
        {children}
      </div>
    </div>
  );
};

export const DangerRow = ({
  label,
  description,
  buttonLabel,
  onClick,
  loading,
  disabled,
  className
}) => {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 px-4 py-3 min-h-[48px]",
        className
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-normal">{label}</p>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <button
        onClick={onClick}
        disabled={loading || disabled}
        className={cn(
          "px-4 py-2 text-sm font-medium rounded-full",
          "bg-destructive/10 text-destructive",
          "active:bg-destructive/20 transition-colors",
          (loading || disabled) && "opacity-50"
        )}
      >
        {loading ? 'Processing...' : buttonLabel}
      </button>
    </div>
  );
};

export const SettingsHeader = ({ title, description, className }) => {
  return (
    <div className={cn("pb-2", className)}>
      <h2 className="text-xl font-semibold">{title}</h2>
      {description && (
        <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
      )}
    </div>
  );
};

export const InputRow = ({
  label,
  description,
  value,
  onChange,
  type = "text",
  placeholder,
  disabled,
  required,
  className
}) => {
  return (
    <div className={cn("px-4 py-3 space-y-2 border-b border-border/30 last:border-b-0", className)}>
      <label className="text-[15px] font-normal">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={cn(
          "w-full h-11 px-3 rounded-xl bg-muted/50 text-[15px]",
          "border-0 focus:outline-none focus:ring-2 focus:ring-primary/30",
          disabled && "opacity-50"
        )}
      />
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  );
};
